import { BALANCE, type Cell, type Json, type Transition } from '@2k48/shared';
import { NodePool } from './NodePool';
import { metrics } from './debug/metrics';
import { dur, gsap } from './motion';

/**
 * Screen and board effects, on pooled nodes.
 *
 * Two things this solves:
 *
 *  1. **No allocation during play.** Effect nodes are created once and
 *     recycled. Creating and destroying a node per hit is style invalidation
 *     plus garbage, per hit, at exactly the moment the frame is busiest.
 *
 *  2. **Effects are open, transitions are closed.** A ruleset emits an
 *     `x:whatever` transition and registers a handler for it here. New rules
 *     ship new visuals without touching the renderer or widening the
 *     `Transition` union.
 */

export interface EffectContext {
  /** Pooled node, already positioned and made visible. */
  node: HTMLElement;
  /** Board position, in px, of the cell the effect was played at. */
  x: number;
  y: number;
  cell: Cell | undefined;
  data: Readonly<Record<string, Json>> | undefined;
  /** Duration helper: already scaled for reduced motion. */
  dur: (seconds: number) => number;
}

/**
 * Returns the timeline that drives the effect. The layer recycles the node when
 * it finishes, so a handler must not hold on to it.
 */
export type EffectHandler = (ctx: EffectContext) => gsap.core.Timeline;

export interface EffectsLayerOptions {
  /** Resolves a board cell to pixels. Supplied by GameArea. */
  positionOf(cell: Cell): { x: number; y: number };
  poolSize?: number;
}

export class EffectsLayer {
  readonly root: HTMLElement;
  private readonly pool: NodePool<HTMLElement>;
  private readonly handlers = new Map<string, EffectHandler>();
  private readonly positionOf: EffectsLayerOptions['positionOf'];

  constructor(container: HTMLElement, options: EffectsLayerOptions) {
    this.positionOf = options.positionOf;

    this.root = document.createElement('div');
    this.root.className = 'fx-layer';
    container.appendChild(this.root);

    this.pool = new NodePool(
      () => {
        const node = document.createElement('div');
        node.className = 'fx';
        return node;
      },
      this.root,
      options.poolSize ?? BALANCE.perf.effectPoolSize,
      'effects',
      // Pool size and concurrency budget are the same number here: the layer
      // never grows, it declines. See BALANCE.perf.effectPoolSize.
      options.poolSize ?? BALANCE.perf.effectPoolSize,
    );

    this.registerBuiltIns();
  }

  register(kind: string, handler: EffectHandler): void {
    this.handlers.set(kind, handler);
  }

  /**
   * Play an effect. Unknown kinds are ignored rather than thrown: a client that
   * is one version behind the server will see transitions it has no handler
   * for, and the correct behaviour is to miss the sparkle, not to crash.
   *
   * A spent budget is ignored the same way, and for the same reason. A cascade
   * of merges can ask for effects faster than they finish; allocating for the
   * tail of that burst buys a node nobody can distinguish on screen, at the
   * cost of a frame in the middle of the busiest moment of the match.
   */
  play(kind: string, cell?: Cell, data?: Readonly<Record<string, Json>>): void {
    const handler = this.handlers.get(kind);
    if (!handler) return;

    const node = this.pool.tryAcquire();
    if (!node) {
      metrics.setEffectsRefused(this.pool.stats.refused);
      return;
    }

    const position = cell ? this.positionOf(cell) : { x: 0, y: 0 };

    node.className = 'fx fx--active';
    gsap.set(node, { x: position.x, y: position.y, scale: 1, opacity: 1, rotation: 0 });

    const timeline = handler({
      node,
      x: position.x,
      y: position.y,
      cell,
      data,
      dur,
    });

    timeline.eventCallback('onComplete', () => {
      node.className = 'fx';
      gsap.set(node, { clearProps: 'transform,opacity' });
      this.pool.release(node);
    });
  }

  /** Route the ruleset-defined half of a transition batch. */
  playTransitions(transitions: readonly Transition[]): void {
    for (const transition of transitions) {
      if (!transition.kind.startsWith('x:')) continue;
      const custom = transition as Extract<Transition, { kind: `x:${string}` }>;
      this.play(custom.kind.slice(2), custom.at, custom.data);
    }
  }

  private registerBuiltIns(): void {
    const { motion } = BALANCE;

    // A ring expanding out of a cell. Scale and opacity only.
    this.register('pulse', ({ node }) => {
      node.classList.add('fx--pulse');
      return gsap
        .timeline()
        .set(node, { scale: 0.6, opacity: 1 })
        .to(node, { scale: 1.8, opacity: 0, duration: dur(0.34), ease: motion.ease.fade });
    });

    // A single square flicking outward. Cheap enough to fire several at once.
    this.register('burst', ({ node, data }) => {
      node.classList.add('fx--burst');
      const angle = typeof data?.angle === 'number' ? data.angle : Math.random() * Math.PI * 2;
      const distance = typeof data?.distance === 'number' ? data.distance : 60;
      return gsap.timeline().to(node, {
        x: `+=${Math.cos(angle) * distance}`,
        y: `+=${Math.sin(angle) * distance}`,
        opacity: 0,
        scale: 0.4,
        duration: dur(0.28),
        ease: motion.ease.fade,
      });
    });
  }

  get stats(): { free: number; busy: number } {
    const { free, busy } = this.pool.stats;
    return { free, busy };
  }

  destroy(): void {
    this.pool.destroy();
    this.root.remove();
  }
}
